import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseModal } from 'assets/images/ic_close_modal.svg';
import { ReactComponent as UploadIcon } from 'assets/images/upload.svg';
import { ReactComponent as TemplateIcon } from 'assets/images/need_template.svg';
import { ReactComponent as DownloadTemplate } from 'assets/images/download_template.svg';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import Dropzone, { FileRejection } from 'react-dropzone';
import { Toast } from 'shared/helpers';
import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { downloadFileSaveAs } from 'shared/helpers/DirectDownload';
import { parseExcelToTemplate, ParsedTemplate } from './excelTemplateParser';

interface IBuldUploadProps {
  isUploadTemplate: boolean;
  onClose: (reload: boolean) => void;
  title: string;
  onDataParsed: (data: ParsedTemplate) => void;
}

export interface IError {
  row: number;
  errors: string[];
}

export interface IUploadError {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: IError[];
}

const UploadExcelTemplate = (props: IBuldUploadProps): any => {
  const { isUploadTemplate, onClose, title, onDataParsed } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [showErrorView, setShowErrorView] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  //   const [errors, setErros] = useState<IUploadError>({
  //     totalRows: 0,
  //     successCount: 0,
  //     errorCount: 0,
  //     errors: [],
  //   });

  // UI Elements
  const actionOnRemoveFile = (index: number): void => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  const actionOnFileSelected = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ): void => {
    if (rejectedFiles.length > 0) {
      Toast.showInfoMessage('Please select one valid xlsx, csv file');
      return;
    }
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const MAX_SIZE = 10;
      if (file.size > MAX_SIZE * 1024 * 1024) {
        Toast.showInfoMessage(`File size cannot be exceed ${MAX_SIZE} MB`);
        return;
      }
    }
    setFiles(acceptedFiles);
  };

  const actionOnUpload = async (): Promise<void> => {
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const parsed = await parseExcelToTemplate(files[0]);

      onDataParsed(parsed);

      setFiles([]);
      onClose(false);
    } catch (error: any) {
      console.error('Error parsing excel file:', error);
      Toast.showErrorMessage(
        error?.message ||
          'Could not read that file. Please check it matches the template format.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const actionOnDownloadTemplate = async (): Promise<void> => {
    let fileUrl = '';
    let fileName = '';

    fileUrl = `${window.location.origin}/templates/Assessment_Template.xlsx`;
    fileName = 'Assessment_Template.xlsx';

    await downloadFileSaveAs(fileUrl, fileName);
  };

  // UI Elements
  return (
    <Dialog open={isUploadTemplate} maxWidth={false}>
      <DialogContent>
        <Box sx={{ background: 'white', padding: '1.5rem', width: '65vw' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3" fontWeight={700}>
              {title}
            </Typography>

            <SvgIcon
              component={CloseModal}
              sx={{
                cursor: 'pointer',
              }}
              onClick={() => {
                onClose(false);
              }}
            />
          </Stack>
          {!showErrorView && (
            <>
              {false && (
                <>
                  {/* Upload details progress */}
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#7E7E7E',
                      }}
                    >
                      <Typography variant="body1">Step 1 of 4</Typography>
                      <Typography variant="body1">25% Complete</Typography>
                    </Box>

                    <Box mt={3}>
                      <LinearProgress
                        variant="determinate"
                        value={25}
                        sx={{
                          height: '20px',
                          borderRadius: 5,
                          backgroundColor: '#D1F27A',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#06362B',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}

              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography variant="h3" fontWeight={700} mt={8}>
                  Upload data file
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  Upload an Excel (.xlsx) file containing your data information.
                </Typography>

                <Dropzone
                  disabled={files.length > 0}
                  maxFiles={1}
                  multiple={false}
                  accept={{
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                  }}
                  //   maxFiles={1}
                  onDrop={actionOnFileSelected}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Box
                        sx={{
                          cursor: 'pointer',
                          border: '1px dashed lightgray',
                          padding: 5,
                        }}
                        mt={8}
                      >
                        {files.map((file, index) => (
                          <Stack
                            justifyContent="center"
                            direction="row"
                            spacing={5}
                            alignItems="center"
                          >
                            <Typography variant="h3">{file.name}</Typography>
                            <IconButton
                              onClick={() => {
                                actionOnRemoveFile(index);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Stack>
                        ))}
                        {files.length === 0 && (
                          <>
                            <UploadIcon />
                            <Typography variant="h3" fontWeight={700} mt={8}>
                              Drag and drop your file here
                            </Typography>
                            <Typography sx={{ color: '#7E7E7E' }}>
                              or click to browse and select a file
                              <br />
                              Supports Excel (.xlsx) files up to 10MB
                            </Typography>
                          </>
                        )}
                      </Box>
                    </div>
                  )}
                </Dropzone>
              </Box>

              <Stack className="mt-5" justifyContent="flex-end" direction="row">
                <AppPrimaryButton
                  className="btn-small"
                  title="Upload"
                  disabled={files.length === 0 || isUploading}
                  onClick={actionOnUpload}
                  isLoading={isUploading}
                />
              </Stack>

              <Box mt={8}>
                <Typography variant="h3" fontWeight={700} mt={8}>
                  <Box>
                    <TemplateIcon />
                    <span style={{ top: '-2px', position: 'relative' }}>
                      {' '}
                      Need a template?
                    </span>
                  </Box>
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  Download our template to ensure your data is formatted
                  correctly for import.
                </Typography>
              </Box>

              <Box mt={8}>
                <AppPrimaryButton
                  title="Download template"
                  className="btn-small"
                  onClick={actionOnDownloadTemplate}
                  startIcon={
                    <SvgIcon
                      component={DownloadTemplate}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  }
                />
              </Box>

              <Box mt={8}>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • File must be in Excel (.xlsx)
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • First row should contain column headers
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • Maximum file size: 10MB
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • Ensure all required fields are included
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • Use the template for best results
                </Typography>
                <Typography sx={{ color: '#7E7E7E' }}>
                  • Use <b>@@</b> to separate multiple options
                </Typography>
              </Box>
            </>
          )}
          {showErrorView && (
            <>
              <Stack alignItems="center" spacing={3}>
                <Typography variant="h3">Validation Failed</Typography>
                <Typography className="text-gray">
                  Please review the validaton errors below
                </Typography>
              </Stack>
              <Box className="app-box-border mt-5 p-5">
                <Grid container spacing={3}>
                  <Grid item md={6} sm={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">File Name</Typography>
                      <Typography className="fw-6">{files[0].name}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item md={6} sm={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">Valid Rows</Typography>
                      <Typography className="fw-6" sx={{ color: 'green' }}>
                        {/* {errors.successCount} */}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item md={6} sm={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">Total Rows</Typography>
                      <Typography className="fw-6">
                        {/* {errors.totalRows} */}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item md={6} sm={6}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography className="text-gray">
                        Invalid Rows
                      </Typography>
                      <Typography className="fw-6" sx={{ color: 'red' }}>
                        {/* {errors.errorCount} */}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
              <Stack className="mt-5" justifyContent="flex-end" direction="row">
                <AppPrimaryButton
                  className="btn-small"
                  title="Fix"
                  onClick={() => {
                    setShowErrorView(false);
                    setFiles([]);
                  }}
                />
              </Stack>
              <Box className="app-box-border mt-5 p-5">
                <Stack direction="row" spacing={4}>
                  <InfoIcon color="error" />
                  <Stack>
                    <Typography className="fw-6">
                      Validation Errors Found
                    </Typography>
                    <Typography className="text-gray">
                      {/* {errors.errorCount} row(s) failed validation. Please fix
                      the errors below and try again */}
                    </Typography>
                  </Stack>
                </Stack>
                {/* {errors.errors.map((error) => (
                  <Stack
                    key={`error_row_${error.row}`}
                    className="app-box-border p-2 mt-5"
                  >
                    <Typography>Row {error.row}:</Typography>
                    {error.errors.map((e) => (
                      <Typography key={e} className="text-gray">
                        {e}
                      </Typography>
                    ))}
                  </Stack>
                ))} */}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadExcelTemplate;
